import { type AssembledImage, assembleSource } from "./assembler.js";
import { setupCharacterEditor, type CharacterEditorElements } from "./character-editor.js";
import { setupDebugger, type DebuggerActions, type DebuggerElements, type DebuggerRuntime } from "./debugger.js";
import { Emulator, MEMORY_SIZE } from "./emulator.js";
import { setupImageEditor, type ImageEditorElements } from "./image-editor.js";
import { setupUserManuals, type UserManualsController } from "./manuals/index.js";
import { setupReferenceGuide } from "./reference/index.js";
import { setupSoundTracker, type SoundTrackerController } from "./sound-tracker/index.js";
import type { SoundTrackerPlaybackHost } from "./sound-tracker/playback.js";
import { setupSpriteEditor, type SpriteEditorElements } from "./sprite-editor.js";
import { setupTileMapEditor, type TileMapEditorElements } from "./tile-map-editor.js";

type WorkbenchTabId = "assembler" | "disassembler" | "debugger" | "sprites" | "characters" | "tilemap" | "images" | "tracker" | "reference" | "manuals";

type WorkbenchElements = DebuggerElements & SpriteEditorElements & CharacterEditorElements & {
  imageEditor: ImageEditorElements;
  tileMapEditor: TileMapEditorElements;
  assembledOutput: HTMLPreElement;
  assemblerSource: HTMLTextAreaElement;
  assemblerStatus: HTMLParagraphElement;
  assembleTrigger: HTMLButtonElement;
  writeMemoryTrigger: HTMLButtonElement;
  disassembledOutput: HTMLPreElement;
  disassemblyStatus: HTMLParagraphElement;
  disassembleTrigger: HTMLButtonElement;
  memoryAddress: HTMLInputElement;
  memoryLength: HTMLInputElement;
  memoryOutput: HTMLPreElement;
  memoryStatus: HTMLParagraphElement;
  memoryTrigger: HTMLButtonElement;
  panelAssembler: HTMLElement;
  panelDisassembler: HTMLElement;
  panelDebugger: HTMLElement;
  panelSprites: HTMLElement;
  panelCharacters: HTMLElement;
  panelTileMap: HTMLElement;
  panelImages: HTMLElement;
  panelTracker: HTMLElement;
  panelReference: HTMLElement;
  panelManuals: HTMLElement;
  trackerHost: SoundTrackerPlaybackHost;
  tabAssembler: HTMLButtonElement;
  tabDisassembler: HTMLButtonElement;
  tabDebugger: HTMLButtonElement;
  tabSprites: HTMLButtonElement;
  tabCharacters: HTMLButtonElement;
  tabTileMap: HTMLButtonElement;
  tabImages: HTMLButtonElement;
  tabTracker: HTMLButtonElement;
  tabReference: HTMLButtonElement;
  tabManuals: HTMLButtonElement;
};

export type WorkbenchController = {
  refreshDebugger(): void;
  refreshCharacters(): void;
  refreshTileMap(): void;
  refreshImages(): void;
  refreshManuals(): void;
  refreshTracker(): void;
  refreshSprites(): void;
  setDebuggerActions(actions: DebuggerActions): void;
  setVisible(visible: boolean): void;
  syncTracker(): void;
  updateDebuggerRuntime(runtime: DebuggerRuntime): void;
};

type MemorySnapshot = {
  address: number;
  bytes: Uint8Array;
};

const ASSEMBLER_BYTES_PER_LINE = 16;
const MEMORY_BYTES_PER_LINE = 16;

function parseWorkbenchNumber(raw: string, label: string): number {
  const text = raw.trim();
  if (text === "") {
    throw new Error(`${label} is required`);
  }

  let value: number;
  if (text.startsWith("$")) {
    value = Number.parseInt(text.slice(1), 16);
  } else {
    value = Number(text);
  }

  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be a finite integer`);
  }
  return value;
}

function formatHexdump(base: number, bytes: Uint8Array, bytesPerLine: number): string {
  if (bytes.length === 0) {
    return "";
  }

  const lines: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += bytesPerLine) {
    const slice = bytes.slice(offset, offset + bytesPerLine);
    const rendered = Array.from(slice, (value) => value.toString(16).toUpperCase().padStart(2, "0")).join(" ");
    lines.push(`0x${(base + offset).toString(16).toUpperCase().padStart(4, "0")}: ${rendered}`);
  }
  return lines.join("\n");
}

function formatMemoryHexdump(base: number, bytes: Uint8Array, bytesPerLine: number): string {
  if (bytes.length === 0) {
    return "";
  }

  const lines: string[] = [];
  const paddedHexWidth = bytesPerLine * 3 - 1;
  for (let offset = 0; offset < bytes.length; offset += bytesPerLine) {
    const slice = bytes.slice(offset, offset + bytesPerLine);
    const hex = Array.from(slice, (value) => value.toString(16).toUpperCase().padStart(2, "0")).join(" ");
    const ascii = Array.from(slice, (value) =>
      value >= 0x20 && value <= 0x7e ? String.fromCharCode(value) : "."
    ).join("");
    lines.push(
      `0x${(base + offset).toString(16).toUpperCase().padStart(4, "0")}: ${hex.padEnd(paddedHexWidth, " ")}  |${ascii}|`
    );
  }
  return lines.join("\n");
}

function setTabState(
  activeTab: WorkbenchTabId,
  tabs: Array<{ id: WorkbenchTabId; button: HTMLButtonElement; panel: HTMLElement }>
): void {
  for (const tab of tabs) {
    const active = tab.id === activeTab;
    tab.button.setAttribute("aria-selected", active ? "true" : "false");
    tab.button.tabIndex = active ? 0 : -1;
    tab.button.classList.toggle("is-active", active);
    tab.panel.hidden = !active;
    tab.panel.classList.toggle("is-active", active);
  }
}

function clearReferenceHash(): void {
  if (!window.location.hash.startsWith("#reference/")) {
    return;
  }
  const url = new URL(window.location.href);
  url.hash = "";
  window.history.replaceState(null, "", url);
}

function renderMemorySnapshot(
  emulator: Emulator,
  memoryAddress: HTMLInputElement,
  memoryLength: HTMLInputElement,
  memoryOutput: HTMLPreElement,
  memoryStatus: HTMLParagraphElement
): MemorySnapshot {
  const address = parseWorkbenchNumber(memoryAddress.value, "memory address");
  const length = parseWorkbenchNumber(memoryLength.value, "memory length");
  if (length <= 0) {
    throw new Error("memory length must be greater than zero");
  }

  const snapshot = emulator.copyMemory(address, length);
  memoryOutput.textContent = formatMemoryHexdump(address, snapshot, MEMORY_BYTES_PER_LINE);
  memoryStatus.textContent = `Read ${snapshot.length} byte${snapshot.length === 1 ? "" : "s"} from 0x${address
    .toString(16)
    .toUpperCase()
    .padStart(4, "0")}.`;
  return { address, bytes: snapshot };
}

export function setupWorkbench(emulator: Emulator, elements: WorkbenchElements): WorkbenchController {
  const {
    assembledOutput,
    assemblerSource,
    assemblerStatus,
    assembleTrigger,
    writeMemoryTrigger,
    disassembledOutput,
    disassemblyStatus,
    disassembleTrigger,
    memoryAddress,
    memoryLength,
    memoryOutput,
    memoryStatus,
    memoryTrigger,
    panelAssembler,
    panelDisassembler,
    panelDebugger,
    panelSprites,
    panelCharacters,
    panelTileMap,
    panelImages,
    panelTracker,
    panelReference,
    panelManuals,
    tabAssembler,
    tabDisassembler,
    tabDebugger,
    tabSprites,
    tabCharacters,
    tabTileMap,
    tabImages,
    tabTracker,
    tabReference,
    tabManuals
  } = elements;

  const debuggerController = setupDebugger(emulator, elements);
  const manualsController: UserManualsController = setupUserManuals(panelManuals);
  const spriteEditorController = setupSpriteEditor(emulator, elements);
  const characterEditorController = setupCharacterEditor(emulator, elements);
  const tileMapEditorController = setupTileMapEditor(emulator, elements.tileMapEditor, {
    getCharacterDraft() {
      return characterEditorController.exportDraftForTileset();
    },
    openTilesetInCharacterEditor(draft) {
      const imported = characterEditorController.importExternalDraft(draft);
      if (!imported) {
        return false;
      }
      activeTab = "characters";
      setTabState("characters", tabs);
      clearReferenceHash();
      return true;
    }
  });
  const imageEditorController = setupImageEditor(emulator, elements.imageEditor);
  const trackerController: SoundTrackerController = setupSoundTracker(emulator, panelTracker, elements.trackerHost);

  const tabs = [
    { id: "assembler" as const, button: tabAssembler, panel: panelAssembler },
    { id: "disassembler" as const, button: tabDisassembler, panel: panelDisassembler },
    { id: "debugger" as const, button: tabDebugger, panel: panelDebugger },
    { id: "sprites" as const, button: tabSprites, panel: panelSprites },
    { id: "characters" as const, button: tabCharacters, panel: panelCharacters },
    { id: "tilemap" as const, button: tabTileMap, panel: panelTileMap },
    { id: "images" as const, button: tabImages, panel: panelImages },
    { id: "tracker" as const, button: tabTracker, panel: panelTracker },
    { id: "reference" as const, button: tabReference, panel: panelReference },
    { id: "manuals" as const, button: tabManuals, panel: panelManuals }
  ];
  let activeTab: WorkbenchTabId = window.location.hash.startsWith("#reference/") ? "reference" : "assembler";
  let latestDebuggerRuntime: DebuggerRuntime = {
    cycleCount: emulator.getCycleCount().toString(),
    debuggerActive: false,
    frameNo: emulator.getFrameNo(),
    state: "paused_manual",
    stopReason: "none"
  };
  let workspaceVisible = false;

  assembledOutput.textContent = "No assembled bytes yet.";
  memoryOutput.textContent = "No memory snapshot yet.";
  disassembledOutput.textContent = "No disassembly yet.";
  setupReferenceGuide(panelReference);
  assemblerStatus.textContent = "";
  memoryStatus.textContent = "Reads a side-effect-free snapshot from the running emulator.";
  disassemblyStatus.textContent = "Click Disassemble to decode the currently shown memory range.";
  let lastAssembledImage: AssembledImage | null = null;

  const renderWriteButton = (): void => {
    writeMemoryTrigger.disabled = lastAssembledImage === null;
  };

  for (const tab of tabs) {
    tab.button.addEventListener("click", () => {
      activeTab = tab.id;
      setTabState(tab.id, tabs);
      trackerController.setVisible(workspaceVisible && tab.id === "tracker");
      if (tab.id === "debugger") {
        debuggerController.updateRuntime(latestDebuggerRuntime);
        debuggerController.refresh();
      }
      if (tab.id === "sprites") {
        spriteEditorController.refresh();
      }
      if (tab.id === "characters") {
        characterEditorController.refresh();
      }
      if (tab.id === "tilemap") {
        tileMapEditorController.refresh();
      }
      if (tab.id === "images") {
        imageEditorController.refresh();
      }
      if (tab.id === "tracker") {
        trackerController.refresh();
      }
      if (tab.id === "manuals") {
        manualsController.refresh();
      }
      if (tab.id !== "reference") {
        clearReferenceHash();
      }
    });
  }
  setTabState(activeTab, tabs);
  trackerController.setVisible(false);
  window.addEventListener("hashchange", () => {
    if (window.location.hash.startsWith("#reference/")) {
      activeTab = "reference";
      setTabState("reference", tabs);
      trackerController.setVisible(false);
    }
  });
  renderWriteButton();

  assembleTrigger.addEventListener("click", () => {
    try {
      const assembled = assembleSource(emulator, assemblerSource.value);
      lastAssembledImage = assembled;
      assembledOutput.textContent = formatHexdump(assembled.base, assembled.bytes, ASSEMBLER_BYTES_PER_LINE);
      assemblerStatus.textContent =
        `Assembled ${assembled.bytes.length} byte${assembled.bytes.length === 1 ? "" : "s"} starting at 0x${assembled.base
          .toString(16)
          .toUpperCase()
          .padStart(4, "0")}.`;
      renderWriteButton();
    } catch (error) {
      lastAssembledImage = null;
      assembledOutput.textContent = "";
      assemblerStatus.textContent = error instanceof Error ? error.message : String(error);
      renderWriteButton();
    }
  });

  writeMemoryTrigger.addEventListener("click", () => {
    if (lastAssembledImage === null) {
      assemblerStatus.textContent = "Assemble bytes first before writing them into emulator memory.";
      return;
    }

    try {
      emulator.writeMemory(lastAssembledImage.base, lastAssembledImage.bytes);
      assemblerStatus.textContent = `Wrote ${lastAssembledImage.bytes.length} byte${lastAssembledImage.bytes.length === 1 ? "" : "s"} into emulator memory at 0x${lastAssembledImage.base
        .toString(16)
        .toUpperCase()
        .padStart(4, "0")}.`;
    } catch (error) {
      assemblerStatus.textContent = error instanceof Error ? error.message : String(error);
    }
  });

  memoryTrigger.addEventListener("click", () => {
    try {
      renderMemorySnapshot(emulator, memoryAddress, memoryLength, memoryOutput, memoryStatus);
    } catch (error) {
      memoryOutput.textContent = "";
      memoryStatus.textContent = error instanceof Error ? error.message : String(error);
    }
  });

  disassembleTrigger.addEventListener("click", () => {
    try {
      const snapshot = renderMemorySnapshot(emulator, memoryAddress, memoryLength, memoryOutput, memoryStatus);
      disassembledOutput.textContent = emulator.disassembleWorkbenchBytes(snapshot.bytes, snapshot.address).trimEnd();
      disassemblyStatus.textContent = `Disassembled ${snapshot.bytes.length} byte${snapshot.bytes.length === 1 ? "" : "s"} from 0x${snapshot.address
        .toString(16)
        .toUpperCase()
        .padStart(4, "0")}.`;
    } catch (error) {
      disassembledOutput.textContent = "";
      disassemblyStatus.textContent = error instanceof Error ? error.message : String(error);
    }
  });

  return {
    refreshDebugger() {
      debuggerController.refresh();
    },
    refreshCharacters() {
      characterEditorController.refresh();
    },
    refreshTileMap() {
      tileMapEditorController.refresh();
    },
    refreshImages() {
      imageEditorController.refresh();
    },
    refreshManuals() {
      manualsController.refresh();
    },
    refreshTracker() {
      trackerController.refresh();
    },
    refreshSprites() {
      spriteEditorController.refresh();
    },
    setDebuggerActions(actions) {
      debuggerController.setActions(actions);
    },
    setVisible(visible) {
      workspaceVisible = visible;
      trackerController.setVisible(visible && activeTab === "tracker");
      if (!visible) {
        return;
      }
      if (activeTab === "debugger") {
        debuggerController.updateRuntime(latestDebuggerRuntime);
        debuggerController.refresh();
      }
      if (activeTab === "sprites") {
        spriteEditorController.refresh();
      }
      if (activeTab === "characters") {
        characterEditorController.refresh();
      }
      if (activeTab === "tilemap") {
        tileMapEditorController.refresh();
      }
      if (activeTab === "images") {
        imageEditorController.refresh();
      }
      if (activeTab === "tracker") {
        trackerController.refresh();
      }
      if (activeTab === "manuals") {
        manualsController.refresh();
      }
    },
    syncTracker() {
      trackerController.syncFromEmulator();
    },
    updateDebuggerRuntime(runtime) {
      latestDebuggerRuntime = runtime;
      if (!workspaceVisible || activeTab !== "debugger") {
        return;
      }
      debuggerController.updateRuntime(runtime);
    }
  };
}
